(() => {
  const MAX_METER_COUNT = 8;
  const INITIAL_CATALOG_BATCH_SIZE = 48;
  const CATALOG_BATCH_SIZE = 48;
  const CORAL_CATEGORIES = [
    {
      key: 'sps',
      label: 'SPS Corals',
      tags: ['SPS', 'Acropora', 'Montipora', 'Birdsnest', 'Stylophora', 'Pocillopora'],
    },
    {
      key: 'soft',
      label: 'Soft Corals',
      tags: ['Soft Coral', 'Zoanthids', 'Green Star Polyps', 'Leather', 'Xenia'],
    },
    {
      key: 'lps',
      label: 'LPS Corals',
      tags: [
        'LPS',
        'Cyphastrea',
        'Favia',
        'Chalice',
        'Leptoseris',
        'Leptastrea',
        'Galaxea',
        'Alveopora',
        'Blastomussa',
        'Goniopora',
        'Acans',
        'Plate Coral',
        'Lithophyllon',
      ],
    },
  ];

  class TopShelfCoralPackBuilder extends HTMLElement {
    constructor() {
      super();
      this.selected = new Map();
      this.busy = false;
      this.lastRenderedCount = null;
      this.catalogProducts = [];
      this.catalogRenderIndex = 0;
      this.renderedCatalogCount = 0;
      this.pendingCatalogRender = null;
      this.abortController = new AbortController();
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.config = this.readConfig();
      this.storageKey = this.dataset.storageKey || 'topshelf-coral-pack';
      this.currencyFormatter = new Intl.NumberFormat(this.config.locale || undefined, {
        style: 'currency',
        currency: this.config.currency || 'USD',
      });

      this.cacheElements();
      this.restore();
      this.addEventListener('click', this.onClick, { signal: this.abortController.signal });
      this.render();
      this.loadAndGroupAllCorals();
    }

    disconnectedCallback() {
      this.cancelCatalogRender();
      this.abortController.abort();
    }

    readConfig() {
      const element = this.querySelector('[data-pack-config]');
      if (!element) return {};

      try {
        return JSON.parse(element.textContent || '{}');
      } catch (error) {
        console.error('Unable to read Coral Pack Builder configuration.', error);
        return {};
      }
    }

    cacheElements() {
      this.countElement = this.querySelector('[data-pack-count]');
      this.pageCountElement = this.querySelector('[data-page-selection-count]');
      this.meterElement = this.querySelector('[data-pack-meter]');
      this.meterFillElement = this.querySelector('[data-pack-meter-fill]');
      this.milestoneElements = this.querySelectorAll('[data-pack-milestone]');
      this.tierStatusElement = this.querySelector('[data-tier-status]');
      this.selectedListElement = this.querySelector('[data-selected-list]');
      this.subtotalElement = this.querySelector('[data-pack-subtotal]');
      this.discountElement = this.querySelector('[data-pack-discount]');
      this.totalElement = this.querySelector('[data-pack-total]');
      this.addButton = this.querySelector('[data-add-pack]');
      this.addButtonLabel = this.querySelector('[data-add-pack-label]');
      this.statusElement = this.querySelector('[data-pack-status]');
      this.catalogStatusElement = this.querySelector('[data-catalog-status]');
      this.catalogElement = this.querySelector('[data-coral-catalog]');
      this.gridElement = this.querySelector('[data-coral-grid]');
      this.summaryElement = this.querySelector('.topshelf-coral-pack__summary');
      this.summaryHeadingElement = this.querySelector('[id^="CoralPackSummary-"]');
      this.mobileDockElement = this.querySelector('[data-mobile-pack-dock]');
      this.mobileCountElement = this.querySelector('[data-mobile-pack-count]');
      this.mobileTierStatusElement = this.querySelector('[data-mobile-tier-status]');
      this.mobileMeterElement = this.querySelector('[data-mobile-pack-meter]');
      this.mobileMeterFillElement = this.querySelector('[data-mobile-pack-meter-fill]');
    }

    onClick = (event) => {
      const selectButton = event.target.closest('[data-select-coral]');
      if (selectButton && this.contains(selectButton)) {
        this.toggleCard(selectButton.closest('[data-coral-variant-id]'));
        return;
      }

      const removeButton = event.target.closest('[data-remove-variant]');
      if (removeButton && this.contains(removeButton)) {
        this.removeSelection(removeButton.dataset.removeVariant);
        return;
      }

      const viewPackButton = event.target.closest('[data-view-pack]');
      if (viewPackButton && this.contains(viewPackButton)) {
        this.scrollToSummary();
        return;
      }

      const addButton = event.target.closest('[data-add-pack]');
      if (addButton && this.contains(addButton)) {
        this.addPack();
      }
    };

    toggleCard(card) {
      if (!card) return;

      const variantId = card.dataset.coralVariantId;
      if (!variantId) return;

      if (this.selected.has(variantId)) {
        this.selected.delete(variantId);
      } else {
        this.selected.set(variantId, this.selectionFromCard(card));
      }

      this.clearStatus();
      this.persist();
      this.render();
    }

    removeSelection(variantId) {
      if (!variantId) return;
      this.selected.delete(variantId);
      this.clearStatus();
      this.persist();
      this.render();
    }

    selectionFromCard(card) {
      return {
        id: String(card.dataset.coralVariantId || ''),
        title: card.dataset.variantTitle || 'Coral',
        priceCents: Number.parseInt(card.dataset.priceCents || '0', 10) || 0,
        sourceUrl: card.dataset.sourceUrl || `${window.location.pathname}${window.location.search}`,
      };
    }

    restore() {
      let stored;

      try {
        stored = JSON.parse(window.sessionStorage.getItem(this.storageKey) || '[]');
      } catch (error) {
        stored = [];
      }

      if (!Array.isArray(stored)) return;

      stored.forEach((item) => {
        if (!item || !item.id || this.selected.has(String(item.id))) return;
        this.selected.set(String(item.id), {
          id: String(item.id),
          title: String(item.title || 'Coral'),
          priceCents: Number.parseInt(item.priceCents, 10) || 0,
          sourceUrl: String(item.sourceUrl || `${window.location.pathname}${window.location.search}`),
        });
      });
    }

    persist() {
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify(Array.from(this.selected.values())));
      } catch (error) {
        // Selection still works for this page when storage is unavailable.
      }
    }

    clearPersistedSelection() {
      this.selected.clear();
      try {
        window.sessionStorage.removeItem(this.storageKey);
      } catch (error) {
        // Nothing else is required when storage is unavailable.
      }
    }

    render() {
      const count = this.selected.size;
      const discountPercent = this.discountPercentForCount(count);
      const subtotalCents = Array.from(this.selected.values()).reduce(
        (total, item) => total + item.priceCents,
        0
      );
      const discountCents = Math.round(subtotalCents * (discountPercent / 100));
      const totalCents = subtotalCents - discountCents;
      const progress = Math.min(count / MAX_METER_COUNT, 1) * 100;

      if (this.countElement) this.countElement.textContent = String(count);
      if (this.meterFillElement) this.meterFillElement.style.width = `${progress}%`;
      if (this.meterElement) {
        this.meterElement.setAttribute('aria-valuenow', String(Math.min(count, MAX_METER_COUNT)));
        this.meterElement.setAttribute(
          'aria-valuetext',
          `${count} corals selected. ${discountPercent}% pack discount.`
        );
      }
      this.renderMilestones(count);
      if (this.tierStatusElement) this.tierStatusElement.textContent = this.tierMessage(count);
      if (this.subtotalElement) this.subtotalElement.textContent = this.money(subtotalCents);
      if (this.discountElement) this.discountElement.textContent = `−${this.money(discountCents)}`;
      if (this.totalElement) this.totalElement.textContent = this.money(totalCents);
      if (this.mobileDockElement) this.mobileDockElement.hidden = count === 0;
      if (this.mobileCountElement) {
        this.mobileCountElement.textContent = `${count} ${count === 1 ? 'coral' : 'corals'} selected`;
      }
      if (this.mobileTierStatusElement) {
        this.mobileTierStatusElement.textContent = this.mobileTierMessage(count);
      }
      if (this.mobileMeterFillElement) this.mobileMeterFillElement.style.width = `${progress}%`;
      if (this.mobileMeterElement) {
        this.mobileMeterElement.setAttribute('aria-valuenow', String(Math.min(count, MAX_METER_COUNT)));
        this.mobileMeterElement.setAttribute(
          'aria-valuetext',
          `${count} corals selected. ${discountPercent}% pack discount.`
        );
      }

      this.renderCards();
      this.renderSelectedList();
      this.renderPageCount();

      if (this.addButton) this.addButton.disabled = this.busy || count === 0;
      if (this.addButtonLabel && !this.busy) {
        this.addButtonLabel.textContent = count
          ? `${this.config.addLabel || 'Add pack to cart'} (${count})`
          : this.config.addLabel || 'Add pack to cart';
      }

      this.lastRenderedCount = count;
    }

    renderMilestones(count) {
      this.milestoneElements.forEach((element) => {
        const milestone = Number.parseInt(element.dataset.packMilestone || '0', 10);
        const isUnlocked = count >= milestone;
        const wasUnlocked = this.lastRenderedCount !== null && this.lastRenderedCount >= milestone;

        element.classList.toggle('is-unlocked', isUnlocked);

        if (isUnlocked && !wasUnlocked && this.lastRenderedCount !== null) {
          element.classList.remove('is-celebrating');
          void element.offsetWidth;
          element.classList.add('is-celebrating');
        }
      });
    }

    renderCards() {
      this.querySelectorAll('[data-coral-variant-id]').forEach((card) => {
        const isSelected = this.selected.has(String(card.dataset.coralVariantId || ''));
        const button = card.querySelector('[data-select-coral]');
        if (!button) return;

        button.setAttribute('aria-pressed', String(isSelected));
        const selectLabel = button.querySelector('[data-select-label]');
        const selectedLabel = button.querySelector('[data-selected-label]');
        if (selectLabel) selectLabel.hidden = isSelected;
        if (selectedLabel) selectedLabel.hidden = !isSelected;
      });
    }

    renderSelectedList() {
      if (!this.selectedListElement) return;

      const fragment = document.createDocumentFragment();
      this.selected.forEach((item) => {
        const listItem = document.createElement('li');
        listItem.className = 'topshelf-coral-pack__selected-item';

        const title = document.createElement('span');
        title.textContent = item.title;
        title.title = item.title;

        const removeButton = document.createElement('button');
        removeButton.className = 'topshelf-coral-pack__selected-remove';
        removeButton.type = 'button';
        removeButton.dataset.removeVariant = item.id;
        removeButton.setAttribute('aria-label', `Remove ${item.title} from pack`);
        removeButton.textContent = '×';

        listItem.append(title, removeButton);
        fragment.append(listItem);
      });

      this.selectedListElement.replaceChildren(fragment);
    }

    renderPageCount() {
      if (!this.pageCountElement) return;

      const visibleSelected = Array.from(this.querySelectorAll('[data-coral-variant-id]')).filter((card) =>
        this.selected.has(String(card.dataset.coralVariantId || ''))
      ).length;

      this.pageCountElement.hidden = visibleSelected === 0;
      this.pageCountElement.textContent = `${visibleSelected} selected`;
    }

    discountPercentForCount(count) {
      if (count >= 8) return 20;
      if (count >= 6) return 15;
      if (count >= 4) return 10;
      return 0;
    }

    tierMessage(count) {
      if (count >= 8) return this.config.tierLabels?.twenty || '20% unlocked — top tier reached.';
      if (count >= 6) {
        return `${this.config.tierLabels?.fifteen || '15% unlocked'} · ${8 - count} more for 20%.`;
      }
      if (count >= 4) {
        return `${this.config.tierLabels?.ten || '10% unlocked'} · ${6 - count} more for 15%.`;
      }
      const remaining = 4 - count;
      return `${remaining} more ${remaining === 1 ? 'coral' : 'corals'} to unlock 10% off.`;
    }

    mobileTierMessage(count) {
      if (count >= 8) return '20% discount unlocked';
      if (count >= 6) return `15% unlocked · ${8 - count} more for 20%`;
      if (count >= 4) return `10% unlocked · ${6 - count} more for 15%`;
      return `${4 - count} more for 10% off`;
    }

    scrollToSummary() {
      if (!this.summaryElement) return;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      this.summaryHeadingElement?.focus({ preventScroll: true });
      this.summaryElement.scrollIntoView({ behavior, block: 'start' });
    }

    money(cents) {
      return this.currencyFormatter.format((Number(cents) || 0) / 100);
    }

    setStatus(message, isError = false) {
      if (!this.statusElement) return;
      this.statusElement.textContent = message;
      this.statusElement.dataset.error = String(isError);
    }

    clearStatus() {
      if (!this.statusElement) return;
      this.statusElement.textContent = '';
      this.statusElement.removeAttribute('data-error');
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      this.toggleAttribute('aria-busy', isBusy);
      if (this.addButtonLabel && isBusy) {
        this.addButtonLabel.textContent = this.config.addingLabel || 'Adding pack…';
      }
      this.render();
    }

    async addPack() {
      if (this.busy || this.selected.size === 0) return;

      this.clearStatus();
      this.setBusy(true);

      try {
        const packId = this.createPackId();
        const items = Array.from(this.selected.values()).map((item) => ({
          id: Number(item.id),
          quantity: 1,
          properties: {
            _topshelf_pack_id: packId,
            _topshelf_pack_source: this.config.source || 'coral-pack-builder-v1',
          },
        }));
        const sectionIds = this.cartSectionIds();
        const body = { items };

        if (sectionIds.length) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }

        const addResponse = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const addResult = await this.parseJsonResponse(addResponse);

        if (!addResponse.ok || addResult.status) {
          throw new Error(addResult.description || addResult.message || 'The coral pack could not be added.');
        }

        const cartResponse = await fetch(this.dataset.cartUrl || '/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        const cart = await this.parseJsonResponse(cartResponse);
        if (!cartResponse.ok) throw new Error('The cart could not be refreshed.');

        const addedCount = this.selected.size;
        this.clearPersistedSelection();
        this.render();
        this.dispatchCartUpdate(cart, addResult.sections || {}, addedCount);
        this.setStatus(this.config.successMessage || 'Your coral pack was added to the cart.');
      } catch (error) {
        this.setStatus(error instanceof Error ? error.message : 'The coral pack could not be added.', true);
      } finally {
        this.setBusy(false);
      }
    }

    async parseJsonResponse(response) {
      const text = await response.text();
      try {
        return JSON.parse(text || '{}');
      } catch (error) {
        return {
          status: response.status,
          message: response.ok ? 'Shopify returned an unexpected response.' : response.statusText,
        };
      }
    }

    async loadAndGroupAllCorals() {
      if (!this.catalogElement || !this.gridElement) {
        if (this.catalogStatusElement) this.catalogStatusElement.textContent = '';
        return;
      }

      try {
        const catalogUrl = this.config.catalogUrl;
        if (!catalogUrl) throw new Error('The coral catalog is not configured.');
        const response = await fetch(catalogUrl, {
          mode: 'cors',
          cache: 'no-store',
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        });
        const payload = await this.parseJsonResponse(response);
        if (!response.ok || !Array.isArray(payload.products)) {
          throw new Error(payload.message || 'The eligible coral catalog could not be loaded.');
        }

        this.cancelCatalogRender();
        this.catalogProducts = payload.products;
        this.catalogRenderIndex = 0;
        this.renderedCatalogCount = 0;
        this.catalogElement.replaceChildren();
        this.renderCatalogBatch(INITIAL_CATALOG_BATCH_SIZE);
      } catch (error) {
        this.cancelCatalogRender();
        if (this.catalogStatusElement) {
          this.catalogStatusElement.textContent =
            'Eligible corals could not be loaded. Refresh the page to try again.';
          this.catalogStatusElement.dataset.error = 'true';
        }
      }
    }

    renderCatalogBatch(batchSize = CATALOG_BATCH_SIZE) {
      if (!this.catalogElement) return;

      const products = this.catalogProducts.slice(
        this.catalogRenderIndex,
        this.catalogRenderIndex + batchSize
      );
      this.catalogRenderIndex += products.length;

      const cards = products.map((product) => this.cardForProduct(product)).filter(Boolean);
      this.renderedCatalogCount += cards.length;
      this.catalogElement.append(...cards);
      this.groupAndSortCorals();
      this.render();

      const hasMore = this.catalogRenderIndex < this.catalogProducts.length;
      if (this.catalogStatusElement) {
        if (hasMore && this.catalogRenderIndex === products.length) {
          this.catalogStatusElement.textContent =
            `${this.renderedCatalogCount} corals ready. Loading the remaining corals…`;
        } else if (!hasMore) {
          this.catalogStatusElement.textContent = this.renderedCatalogCount
            ? `${this.renderedCatalogCount} eligible corals, grouped by type.`
            : this.config.emptyMessage || 'No eligible corals are currently available.';
        }
      }

      if (hasMore) this.scheduleCatalogRender();
    }

    scheduleCatalogRender() {
      if (this.pendingCatalogRender) return;

      const renderNextBatch = () => {
        this.pendingCatalogRender = null;
        this.renderCatalogBatch();
      };

      if ('requestIdleCallback' in window) {
        const id = window.requestIdleCallback(renderNextBatch, { timeout: 500 });
        this.pendingCatalogRender = { type: 'idle', id };
      } else {
        const id = window.setTimeout(renderNextBatch, 16);
        this.pendingCatalogRender = { type: 'timeout', id };
      }
    }

    cancelCatalogRender() {
      if (!this.pendingCatalogRender) return;

      if (
        this.pendingCatalogRender.type === 'idle' &&
        'cancelIdleCallback' in window
      ) {
        window.cancelIdleCallback(this.pendingCatalogRender.id);
      } else {
        window.clearTimeout(this.pendingCatalogRender.id);
      }
      this.pendingCatalogRender = null;
    }

    cardForProduct(product) {
      const variantId = String(product?.variantId || '');
      const title = String(product?.title || '').trim();
      const priceCents = Number.parseInt(product?.priceCents, 10);
      if (!/^\d+$/.test(variantId) || !title || !Number.isFinite(priceCents)) return null;

      const variantTitle = String(product?.variantTitle || '').trim();
      const variantLabel =
        variantTitle && !['Regular', 'Default Title'].includes(variantTitle)
          ? `${title} — ${variantTitle}`
          : title;
      const productUrl =
        typeof product.productUrl === 'string' && product.productUrl.startsWith('/products/')
          ? product.productUrl
          : '#';

      const card = document.createElement('article');
      card.className = 'topshelf-coral-pack__card';
      card.dataset.coralVariantId = variantId;
      card.dataset.variantTitle = variantLabel;
      card.dataset.priceCents = String(priceCents);
      card.dataset.coralTags = JSON.stringify(Array.isArray(product.tags) ? product.tags : []);
      card.dataset.sourceUrl = productUrl;

      const media = document.createElement('a');
      media.className = 'topshelf-coral-pack__media';
      media.href = productUrl;
      media.setAttribute('aria-label', variantLabel);
      if (typeof product.imageUrl === 'string' && product.imageUrl) {
        const image = document.createElement('img');
        image.className = 'topshelf-coral-pack__image';
        image.src = product.imageUrl;
        image.alt = title;
        image.loading = 'lazy';
        image.decoding = 'async';
        media.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'topshelf-coral-pack__image topshelf-coral-pack__placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        media.append(placeholder);
      }

      const body = document.createElement('div');
      body.className = 'topshelf-coral-pack__card-body';
      const headingWrap = document.createElement('div');
      const heading = document.createElement('h3');
      heading.className = 'topshelf-coral-pack__product-title';
      const productLink = document.createElement('a');
      productLink.href = productUrl;
      productLink.textContent = title;
      heading.append(productLink);
      headingWrap.append(heading);
      if (variantTitle && !['Regular', 'Default Title'].includes(variantTitle)) {
        const subtitle = document.createElement('p');
        subtitle.className = 'topshelf-coral-pack__variant-title';
        subtitle.textContent = variantTitle;
        headingWrap.append(subtitle);
      }

      const meta = document.createElement('div');
      meta.className = 'topshelf-coral-pack__card-meta';
      const price = document.createElement('span');
      price.className = 'topshelf-coral-pack__price';
      price.textContent = this.money(priceCents);
      meta.append(price);

      const button = document.createElement('button');
      button.className = 'topshelf-coral-pack__select';
      button.type = 'button';
      button.setAttribute('aria-pressed', 'false');
      button.dataset.selectCoral = '';
      const selectLabel = document.createElement('span');
      selectLabel.dataset.selectLabel = '';
      selectLabel.textContent = this.config.selectLabel || 'Add to pack';
      const selectedLabel = document.createElement('span');
      selectedLabel.dataset.selectedLabel = '';
      selectedLabel.textContent = this.config.selectedLabel || 'Selected';
      selectedLabel.hidden = true;
      button.append(selectLabel, selectedLabel);

      body.append(headingWrap, meta, button);
      card.append(media, body);
      return card;
    }

    groupAndSortCorals() {
      if (!this.catalogElement) return;

      const cards = Array.from(this.catalogElement.querySelectorAll('[data-coral-variant-id]'));
      const groups = new Map();
      const otherCategory = { key: 'other', label: 'Other Corals', tags: [] };

      cards.forEach((card) => {
        const category = this.categoryForCard(card) || otherCategory;
        if (!groups.has(category.label)) groups.set(category.label, { category, cards: [] });
        groups.get(category.label).cards.push(card);
      });

      const fragment = document.createDocumentFragment();
      [...CORAL_CATEGORIES, otherCategory].forEach((category, categoryIndex) => {
        const group = groups.get(category.label);
        if (!group) return;

        group.cards.sort((left, right) =>
          (left.dataset.variantTitle || '').localeCompare(
            right.dataset.variantTitle || '',
            this.config.locale || undefined,
            { sensitivity: 'base' }
          )
        );

        const categorySection = document.createElement('section');
        categorySection.className = 'topshelf-coral-pack__category';
        categorySection.dataset.coralCategory = category.key;

        const headingId = `${this.id}-category-${categoryIndex}`;
        const heading = document.createElement('h3');
        heading.className = 'topshelf-coral-pack__category-heading';
        heading.id = headingId;

        const label = document.createElement('span');
        label.textContent = category.label;
        const count = document.createElement('small');
        count.textContent = String(group.cards.length);
        count.setAttribute('aria-label', `${group.cards.length} corals`);
        heading.append(label, count);

        const grid = document.createElement('div');
        grid.className = 'topshelf-coral-pack__grid';
        grid.setAttribute('aria-labelledby', headingId);
        grid.append(...group.cards);

        categorySection.append(heading, grid);
        fragment.append(categorySection);
      });

      this.catalogElement.replaceChildren(fragment);
    }

    categoryForCard(card) {
      let tags = [];
      try {
        tags = JSON.parse(card.dataset.coralTags || '[]');
      } catch (error) {
        tags = [];
      }
      return CORAL_CATEGORIES.find((category) =>
        category.tags.some((categoryTag) => tags.includes(categoryTag))
      );
    }

    cartSectionIds() {
      const ids = new Set();
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
        if (component.dataset.sectionId) ids.add(component.dataset.sectionId);
      });
      return Array.from(ids);
    }

    createPackId() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      const random = window.crypto?.getRandomValues
        ? window.crypto.getRandomValues(new Uint32Array(2)).join('')
        : Math.random().toString(36).slice(2);
      return `pack-${Date.now().toString(36)}-${random}`;
    }

    dispatchCartUpdate(cart, sections, addedCount) {
      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.id,
            data: {
              source: 'topshelf-coral-pack-builder',
              itemCount: cart.item_count,
              addedCount,
              sections,
            },
          },
        })
      );
    }
  }

  if (!customElements.get('topshelf-coral-pack-builder')) {
    customElements.define('topshelf-coral-pack-builder', TopShelfCoralPackBuilder);
  }
})();
